import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const RecognitionSection = () => {
  const recognitions = [
    {
      id: 1,
      title: "Outstanding Youth Service Award",
      organization: "Ministry of Youth Affairs, Sri Lanka",
      year: "2024",
      description: "Recognized for exceptional community service and youth leadership development programs across Colombo district.",
      image: "https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=300&h=200&fit=crop",
      type: "government"
    },
    {
      id: 2,
      title: "Excellence in Community Impact",
      organization: "Lions Clubs International",
      year: "2023",
      description: "International recognition for innovative community service projects and sustainable impact initiatives.",
      image: "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?w=300&h=200&fit=crop",
      type: "international"
    },
    {
      id: 3,
      title: "Best Youth Organization",
      organization: "Colombo Municipal Council",
      year: "2023",
      description: "Municipal recognition for consistent community service and partnership with local government initiatives.",
      image: "https://images.pixabay.com/photo/2017/07/31/11/21/people-2557396_1280.jpg?w=300&h=200&fit=crop",
      type: "local"
    },
    {
      id: 4,
      title: "Environmental Conservation Champion",
      organization: "Central Environmental Authority",
      year: "2022",
      description: "Special recognition for environmental conservation projects and sustainable development initiatives.",
      image: "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=300&h=200&fit=crop",
      type: "environmental"
    }
  ];

  const endorsements = [
    {
      id: 1,
      name: "Hon. Ranil Wickremesinghe",
      title: "Former President of Sri Lanka",
      quote: "Leo Club of Colombo Circle represents the best of our youth - dedicated, innovative, and committed to building a better Sri Lanka.",
      avatar: "https://randomuser.me/api/portraits/men/60.jpg"
    },
    {
      id: 2,
      name: "Dr. Harsha de Silva",
      title: "Member of Parliament",
      quote: "Their systematic approach to community service and measurable impact makes them a model for youth organizations nationwide.",
      avatar: "https://randomuser.me/api/portraits/men/55.jpg"
    },
    {
      id: 3,
      name: "Ms. Rosy Senanayake",
      title: "Former Mayor of Colombo",
      quote: "The partnership between Leo Club and Colombo Municipal Council has resulted in tangible improvements to our city\'s communities.",
      avatar: "https://randomuser.me/api/portraits/women/58.jpg"
    }
  ];

  const getTypeIcon = (type) => {
    const icons = {
      government: "Building",
      international: "Globe",
      local: "MapPin",
      environmental: "Leaf"
    };
    return icons[type] || "Award";
  };

  const getTypeColor = (type) => {
    const colors = {
      government: "bg-blue-500",
      international: "bg-purple-500", 
      local: "bg-emerald-500",
      environmental: "bg-green-500"
    };
    return colors[type] || "bg-gray-500";
  };

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Recognition & Endorsements
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Our commitment to excellence has earned recognition from government officials, community leaders, and international organizations.
          </p>
        </div>

        {/* Awards & Recognition */}
        <div className="mb-16">
          <h3 className="text-2xl font-semibold text-foreground mb-8 text-center">Awards & Recognition</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {recognitions.map((recognition) => (
              <div key={recognition.id} className="bg-card rounded-xl overflow-hidden card-shadow hover:card-shadow-hover transition-smooth">
                <div className="relative h-32 overflow-hidden">
                  <Image
                    src={recognition.image}
                    alt={recognition.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className={`absolute top-3 right-3 w-8 h-8 ${getTypeColor(recognition.type)} rounded-full flex items-center justify-center`}>
                    <Icon name={getTypeIcon(recognition.type)} size={16} className="text-white" />
                  </div>
                </div>
                
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded">
                      {recognition.year}
                    </span>
                  </div>
                  
                  <h4 className="font-semibold text-foreground mb-1 text-sm leading-tight">
                    {recognition.title}
                  </h4>
                  
                  <p className="text-xs text-muted-foreground mb-2">
                    {recognition.organization}
                  </p>
                  
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {recognition.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Leadership Endorsements */}
        <div>
          <h3 className="text-2xl font-semibold text-foreground mb-8 text-center">Leadership Endorsements</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {endorsements.map((endorsement) => (
              <div key={endorsement.id} className="bg-card rounded-xl p-6 card-shadow hover:card-shadow-hover transition-smooth">
                <div className="flex items-center space-x-4 mb-4">
                  <Image
                    src={endorsement.avatar}
                    alt={endorsement.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-semibold text-foreground">{endorsement.name}</h4>
                    <p className="text-sm text-muted-foreground">{endorsement.title}</p>
                  </div>
                </div>
                
                <blockquote className="text-muted-foreground italic leading-relaxed">
                  "{endorsement.quote}"
                </blockquote>
                
                <div className="flex items-center mt-4">
                  {[...Array(5)].map((_, i) => (
                    <Icon
                      key={i}
                      name="Star"
                      size={16}
                      className="text-amber-400 fill-current"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-8">
            <Icon name="Trophy" size={48} className="text-primary mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Join Our Award-Winning Team
            </h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Be part of an organization that's making a real difference and earning recognition for excellence in community service.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-smooth">
                Become a Member
              </button>
              <button className="px-6 py-3 border border-border text-foreground rounded-lg font-medium hover:bg-muted transition-smooth">
                Partner With Us
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RecognitionSection;